import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';

import { LOGIN_WITH_TWITTER } from '../../http/apollo-queries';

import { useSession } from '../../context/Session';
import { useAuth } from '../../context/Auth';
import { useModalContext } from '../../context/Modal';
import { useSignInContext } from './SignInContext';

import { Button, Loading } from '../../components/UI';

import { useUrlQuery } from '../../hooks/use-url-query';
import { logger } from '../../helpers/logger';

const SignInWithTwitter = () => {
  const { t } = useTranslation();
  const session = useSession();
  const query = useUrlQuery();
  const { signIn } = useAuth();
  const { dispatch } = useSignInContext();
  const { dispatch: setModal, closeModal } = useModalContext();

  const apiPath = process.env.REACT_APP_TWITTER_API_URI;
  const callbackUrl = window.location.origin + '/';

  const [loginWithTwitter] = useMutation(LOGIN_WITH_TWITTER);

  const login = () => {
    // console.log('SignInWithTwitter - Step1');

    setModal({
      isFullScreenElement: true,
      className: 'bg-grey-90 bg-opacity-10',
      element: (
        <Loading
          className="w-32 h-16 m-auto text-primary"
          size="large"
          status
        />
      ),
    });

    axios({
      url: `${apiPath}/oauth/request_token`,
      data: { callbackUrl },
      method: 'POST',
    })
      .then((res) => {
        logger.log('SignInWithTwitter - authenticate', res);
        const { oauth_token, oauth_token_secret } = res.data;

        session.setSessionItem('oauth_token', oauth_token);
        session.setSessionItem('oauth_token_secret', oauth_token_secret);

        window.location.href = `https://api.twitter.com/oauth/authenticate?oauth_token=${oauth_token}`;
      })
      .catch((err) => {
        logger.error('SignInWithTwitter', err);
      })
      .finally(closeModal);
  };

  useEffect(() => {
    const oauth_token = query.get('oauth_token');
    const oauth_verifier = query.get('oauth_verifier');

    const oauth_token_secret = session.getSessionItem('oauth_token_secret');
    const oauth_access_token = session.getSessionItem('oauth_access_token');
    // const oauth_access_token_secret = session.getSessionItem(
    //   'oauth_access_token_secret'
    // );

    // console.log('SignInWithTwitter - fromSession', {
    //   oauth_token_secret,
    //   oauth_access_token,
    //   oauth_access_token_secret,
    // });
    // console.log('SignInWithTwitter - fromQuery', {
    //   oauth_token,
    //   oauth_verifier,
    // });

    if (
      oauth_token &&
      oauth_verifier &&
      oauth_token_secret &&
      !oauth_access_token
    ) {
      setModal({
        isFullScreenElement: true,
        className: 'bg-grey-90 bg-opacity-10',
        element: (
          <Loading
            className="w-32 h-16 m-auto text-primary"
            size="large"
            status
          />
        ),
      });

      axios({
        url: `${apiPath}/oauth/access_token`,
        method: 'POST',
        data: { oauth_token, oauth_verifier, oauth_token_secret },
      })
        .then((res) => {
          logger.log('SignInWithTwitter - getAccessToken', res.data);

          const { oauth_access_token, oauth_access_token_secret } = res.data;

          session.setSessionItem('oauth_access_token', oauth_access_token);
          session.setSessionItem(
            'oauth_access_token_secret',
            oauth_access_token_secret
          );

          //Authenticated Resource Access
          axios({
            url: `${apiPath}/oauth/profile`,
            method: 'POST',
            data: { oauth_access_token, oauth_access_token_secret },
          })
            .then((res) => {
              const { id, email } = res.data;

              logger.log('SignInWithTwitter - getProfile', res.data);

              loginWithTwitter({
                variables: {
                  email,
                  twitterId: id,
                  token: oauth_access_token,
                },
              })
                .then((res: any) => {
                  const { otpPassword } = res.data?.loginWithTwitter;

                  return signIn(email, otpPassword);
                })
                .then((res: any) => {
                  dispatch({
                    type: 'SET_LOGGED',
                    payload: true,
                  });

                  session.clearSessionItems();
                })
                .catch((err) => {
                  logger.error('signInWithTwitter - error', err);
                  toast.error(
                    t('signIn.form.signInWithTwitterError.' + err.message)
                  );
                })
                .finally(closeModal);
            })
            .catch((e) => {
              closeModal();
              logger.log(e);
            });
        })
        .catch((err) => {
          closeModal();
          logger.error(err);
        });
    }
  }, []);

  return (
    <Button
      type="button"
      buttonClass="border border-grey-30 w-full font-semibold dark:text-white text-black mt-4"
      onClick={login}
    >
      {t('signIn.form.loginWithTwitter')}
      <img alt="twitter" src="/twitter.svg" className="ml-auto" />
    </Button>
  );
};

export default SignInWithTwitter;
